import { SPINNER_DOLPHIN } from '../sim/dolphins';
import type { TagSpecies } from '../sim/tags';
import type { FishSpeciesId } from '../sim/fish';
import type { VisitorSpecies } from '../sim/visits';
import { FISH_LIVES } from '../sim/fish';
import { displayName, speciesOf } from '../sim/species';
import { ISLAND_NAMES, REEF_ISLANDS } from '../sim/reef';
import { dateLabel, epochDayFromCivil } from '../sim/calendar';
import { HONU, type Span } from '../sim/turtles';
import { MANTA, MORAY } from '../sim/reefResidents';
import { clockLabel } from './observation';
import { DEEP7_SPECIES, type Deep7SpeciesId, DEEP7_MAUI_NUI_HARD_KM2 } from '../sim/bottomfish';

/**
 * The wildlife card (M97, the brief's sections 5 and 8: "species, the population or cohort it represents, its
 * presence here now, habitat, activity, trend"): what a school the player follows stands for, told in plain words;
 * a humpback's since M99 (`whaleCard`). Pure, tested in node (`tests/wildlife.test.ts`, `tests/whales.test.ts`); the
 * HUD shows it and the camera follows the school or the whale.
 *
 * Each activity is the drawing's own state named by the evidence its day is drawn from (`fishKinds.ts`): never a
 * behavior the drawing does not have, and never a claim the simulation makes, as it runs by the day.
 */

/** The reef fish the game counts and does not simulate, drawn on the Reef (M231; `CountedReefFishId`, `src/render/fishSchools.ts`). */
export type CountedFishId = 'yellowTang' | 'moray';
const COUNTED: readonly string[] = ['yellowTang', 'moray'] satisfies readonly CountedFishId[];
/** Whether a followed school's species is counted, not simulated: no stock, no tag, no trend of its own. */
export const isCountedFish = (species: FishSpeciesId | CountedFishId): species is CountedFishId => COUNTED.includes(species);

/** Each species' names as the card gives them: the registry's (M160, `src/sim/species.ts`). */
export const SPECIES_NAMES: Readonly<Record<FishSpeciesId | CountedFishId, string>> = Object.fromEntries([...FISH_LIVES.map((l) => l.id), ...COUNTED].map((id) => [id, displayName(speciesOf(id as FishSpeciesId | CountedFishId))])) as Record<FishSpeciesId | CountedFishId, string>;

/** Where each drawn species' day comes from (a species not drawn has no school to follow). */
const DAY_SOURCES: Readonly<Partial<Record<FishSpeciesId | CountedFishId, string>>> = {
  bullethead: 'Meyer 2006; Tricas and Boyle 2021',
  palenose: 'Hobson 1972',
  roi: 'Meyer 2008',
  kala: 'Meyer 2003',
  omilu: 'Friedlander, Filous and Sparks, Molokini; Potts 1980, as Sudekum et al. 1991 give it',
  ulua: 'Evans 2021; the Hawaiʻi Institute of Marine Biology\'s tracking; Sudekum et al. 1991',
  redlip: 'Hobson 1972 and 1974; Annandale et al. 2024, Puakō',
  nenue: 'Hobson 1974',
  moanokea: 'Hobson 1974',
  yellowTang: 'Hobson 1972 and 1974',
  moray: 'Hobson 1974, and 1972 for the diurnal fishes\' twilight',
  manini: 'Hobson 1972 and 1974, the surgeonfishes\'',
  taape: 'Hobson 1972 and 1974, the night foragers\', and Parrish et al. 2000',
};

/** A followed school as the view reports it (`ExhibitView.schoolAt`). */
export interface FollowedSchool {
  species: FishSpeciesId | CountedFishId;
  island: number;
  sprites: number;
  fish: number;
  perSprite: number;
  /** Its kind's sprites at the aquarium's widest and the fish each stands for there, and the share drawn at the camera's level of detail now (M105). */
  spritesOfKind: number;
  kindPerSprite: number;
  level: number;
  depthM: number;
  share: number;
  awake: number;
  travelling: number;
  /**
   * In a habitat's own scene (M110): the habitat it shows ("Maui Nui Reef", M118), the kind's adults a hectare of the
   * school's island's reef holds (the simulation's fish are by island), and how many times its length a fish is drawn
   * there (M117's `drawnTimes`); a school of young (M125, `YOUNG_SEEN_MM`) its kind's young a hectare of the whole reef
   * holds; none in the whole tank.
   */
  local?: { place: string; perHa: number; drawnTimes: number; young?: boolean };
}

/** A species' adults over the days the player has watched, for its trend: its first and its latest. */
export interface Seen {
  firstDay: number;
  firstAdults: number;
  lastDay: number;
  lastAdults: number;
}

export interface WildlifeCard {
  title: string;
  rows: readonly (readonly [string, string])[];
  /** Whether it is a migrant whose ways the world map draws (M99). */
  route?: boolean;
  /** The way on with it into another habitat, the button's words (M147, the user's word: "allow players to sometimes follow an animal into a different habitat if it happens to be travelling there"). */
  onward?: string;
  /** What a tag of it would take (M148): one of its kind and stage, a reef stock's on its island; none where it cannot be tagged. */
  tag?: { species: TagSpecies; island?: number; stage: 'adult' | 'young'; minLengthMm?: number; cls?: number };
  /** Its species in the field guide (M160), where no tag names it (a visitor's card). */
  species?: string;
}

/** What a school is doing, by its kind's day as drawn. */
export function activityOf(s: FollowedSchool): string {
  if (s.species === 'roi') {
    if (s.awake < 0.05) return 'resting in its shelter for the night';
    if (s.awake < 0.95) return 'settling into, or leaving, its shelter';
    return 'patrolling its own patch of reef';
  }
  if (s.species === 'omilu') {
    if (s.awake < 0.05) return 'resting on the reef for the night';
    if (s.awake < 0.95) return 'waking at dawn or settling at dusk, when it hunts the most';
    return 'hunting along the reef by day';
  }
  if (s.species === 'ulua') {
    if (Math.abs(s.travelling) > 0.3) return 'crossing its home range, 3.8 to 5.4 km long, between its day and night grounds';
    return s.share > 0.5 ? 'at its day ground, hunting along the reef' : 'at its night ground';
  }
  if (s.species === 'nenue') {
    if (s.awake < 0.05) return 'resting on the reef for the night';
    if (s.awake < 0.95) return 'waking at dawn or settling at dusk';
    return 'cropping algae from the reef with its group';
  }
  if (s.species === 'manini') {
    if (s.awake < 0.05) return 'resting on the reef for the night';
    if (s.awake < 0.95) return 'rising off, or dropping to, the reef in the twilight';
    return 'grazing fine algae in its school as it moves across the reef';
  }
  if (s.species === 'taape') {
    if (s.share > 0.95) return 'hunting crabs and other small animals near the bottom by night';
    if (s.share > 0.05) return 'dispersing from, or gathering back into, its school in the twilight';
    return 'hovering in its school by the reef by day, resting';
  }
  if (s.species === 'moray') {
    if (s.awake < 0.05) return 'under cover in the reef for the night';
    if (s.awake < 0.95) return 'coming out of, or going into, the reef in the twilight';
    return 'prowling the reef by day, seeking out fish sheltering in its crevices';
  }
  if (s.species === 'yellowTang') {
    if (s.awake < 0.05) return 'resting nestled among the coral for the night';
    if (s.awake < 0.95) return 'rising off, or dropping to, the reef in the twilight';
    return 'grazing fine algae over the reef by day';
  }
  if (s.species === 'moanokea') {
    if (s.awake < 0.05) return 'resting under reef cover for the night';
    if (s.awake < 0.95) return 'waking at dawn or settling at dusk';
    return 'probing the reef with its long barbels for small fish';
  }
  if (s.species === 'kala') {
    if (s.share < 0.05) return 'quiet in its refuge hole for the night';
    if (s.share < 0.95) return s.travelling >= 0 ? 'commuting out to its day ground' : 'commuting home to its night refuge';
    return 'foraging in its school on the reef';
  }
  if (s.awake < 0.05) return 'sheltering on the reef for the night';
  if (s.travelling > 0.3) return 'making its way out to its feeding ground';
  if (s.travelling < -0.3) return 'making its way home to its shelter';
  return s.share > 0.5 ? 'grazing on its feeding ground' : 'milling near its shelter';
}

/** A trend in words: the share up or down between the first and the latest days watched, or too soon to say. */
export function trendOf(seen: Seen | null): string {
  if (!seen || seen.lastDay - seen.firstDay < 30 || seen.firstAdults <= 0) return 'too soon to tell: watched for under 30 days';
  const change = (100 * (seen.lastAdults - seen.firstAdults)) / seen.firstAdults;
  const days = seen.lastDay - seen.firstDay;
  const way = Math.abs(change) < 0.5 ? 'steady' : change > 0 ? `up ${change.toFixed(change >= 10 ? 0 : 1)}%` : `down ${(-change).toFixed(-change >= 10 ? 0 : 1)}%`;
  return `${way} over the ${days.toLocaleString('en-US')} days watched (adults; this session's record)`;
}

/** The card for a followed school: its species, what it stands for, its presence, its habitat, its activity and its trend. */
export function wildlifeCard(s: FollowedSchool, seen: Seen | null): WildlifeCard {
  const island = ISLAND_NAMES[REEF_ISLANDS[s.island] ?? 'maui'];
  const n = (x: number): string => x.toLocaleString('en-US');
  const these = s.sprites === 1 ? `This sprite stands for ${n(s.fish)} fish` : `These ${s.sprites} sprites stand for ${n(s.fish)} fish, ${n(s.perSprite)} each`;
  const thinned = s.level < 1 ? `; this close, ${Math.round(s.level * 100)}% of them, each standing for more, so they do not crowd the view, this school all its own while it is followed` : '';
  const local = s.local;
  const perHa = local ? local.perHa.toFixed(local.perHa >= 10 ? 0 : 1) : '';
  const counted = isCountedFish(s.species);
  return {
    title: local?.young ? `${SPECIES_NAMES[s.species]}, young` : SPECIES_NAMES[s.species],
    rows: [
      ['Stands for', counted && local
        ? `${these}; this hectare holds ${n(s.spritesOfKind)} of them, its adults and its young of 10 cm or more, ${island}'s ${perHa} a hectare of every size, NOAA's divers' count on its hard bottom spread over its reef (counted, not simulated: the game holds its count)${s.species === 'moray' ? `, the ones out in the open: baited cameras find about ${Math.round(MORAY.cameraPerHa)} a hectare of the shallow reef they survey on the main islands, against the divers' ${MORAY.perHa.toFixed(1)} a hectare of hard bottom (Zill 2024), most hidden in the reef's holes` : ''}${thinned}`
        : local?.young
        ? `${these}; this hectare holds ${n(s.spritesOfKind)} of its kind's young of 10 cm or more, Maui Nui's reef ${perHa} a hectare${thinned}`
        : local
        ? `${these}; this hectare holds ${n(s.spritesOfKind)} of its kind's adults, ${island}'s ${perHa} a hectare (the simulation's fish are by island, not by place on it)${thinned}`
        : `${these}; the aquarium draws ${n(s.spritesOfKind)} of its kind, ${n(s.spritesOfKind * s.kindPerSprite)} fish, the simulation's every one${thinned}`],
      ['Here', `resident all year on Maui Nui's reefs`],
      ['Habitat', local ? `${local.place}, ${Math.round(s.depthM)} m deep now in its scene, drawn ${local.drawnTimes} times its length to be seen` : `${island}'s reef, ${Math.round(s.depthM)} m deep now (drawn stretched, its depth read true)`],
      ['Activity', `${activityOf(s)}${DAY_SOURCES[s.species] ? ` (its day drawn from ${DAY_SOURCES[s.species]})` : ''}`],
      ['Trend', counted ? 'held at NOAA\'s divers\' count: counted, not simulated, so it neither grows nor falls in the game' : trendOf(seen)],
    ],
  };
}

/**
 * A school's card with the one fish followed told first (M266, Follow Animal; the brief: "the followed animal is a
 * representative visual identity, not necessarily a uniquely simulated individual"): its stage and its stage's mean
 * length today as the day's data gives it, and what it stands for. No life of its own is told: the simulation holds its
 * kind by numbers, not fish by fish.
 */
export function oneFishCard(card: WildlifeCard, f: { lengthMm: number; terminal: boolean; young: boolean; perSprite: number; counted: boolean }): WildlifeCard {
  // A counted kind (M231) held as one count of every size, no stage, its length the divers' mean of those they counted
  // at the sizes drawn (review 31: told "an adult", its length "its stage's mean here today"; its second round: not the
  // mean of all counted, `drawn.meanTlCm` from `drawn.fromCm` up).
  const stage = f.counted ? 'one of its kind' : f.young ? 'a young fish' : f.terminal ? 'a terminal-phase male' : 'an adult';
  const length = f.lengthMm > 0 ? `, about ${Math.round(f.lengthMm / 10)} cm (${f.counted ? 'the mean length of those NOAA\'s divers counted at the sizes drawn' : 'its stage\'s mean here today'})` : '';
  // Short, for a phone's card (the brief's "compact"): what it may do as the days go is the docs' (`docs/PERSISTENT_ANIMALS.md`).
  const row: readonly [string, string] = ['This fish', `${stage}${length}; a representative standing for ${Math.round(f.perSprite).toLocaleString('en-US')} fish, not one the simulation tracks`];
  return { ...card, rows: [row, ...card.rows] };
}

/** A followed whale as the view and the simulation report it (M99): its sprites, the whales they stand for, its depth. */
export interface FollowedWhale {
  depthM: number;
  /** Whether a calf is drawn beside it. */
  calf: boolean;
  sprites: { whales: number; calves: number };
  perSprite: number;
  inHawaii: number;
  calvesInHawaii: number;
  population: number;
}

/** The card for a followed humpback (M99): what it stands for, its season here, its habitat, its activity, its trend and its journey. */
export function whaleCard(w: FollowedWhale): WildlifeCard {
  const n = (x: number): string => Math.round(x).toLocaleString('en-US');
  const plural = (k: number, one: string, many: string): string => `${k} ${k === 1 ? one : many}`;
  const calves = w.sprites.calves > 0 ? ` and ${plural(w.sprites.calves, 'calf', 'calves')} for the ${n(w.calvesInHawaii)} calves among them${w.calf ? ', one beside it' : ''}` : '';
  return {
    title: displayName(speciesOf('humpback')),
    route: true,
    rows: [
      ['Stands for', `This sprite stands for ${n(w.perSprite)} of the ${n(w.inHawaii)} whales in Hawaiʻi's waters today, of the Hawaiʻi population's ${n(w.population)}: the aquarium draws a sprite for each ${n(w.perSprite)}, rounded, ${plural(w.sprites.whales, 'whale', 'whales')}${calves}`],
      ['Here', 'in Hawaiʻi\'s waters December to May, most in February and March, few from June to October (NOAA\'s model of its surveys, Becker et al. 2022, Table 9); Maui Nui\'s share of them no source read gives, so these are Hawaiʻi\'s'],
      ['Habitat', `the shallows within the 183 m contour, where almost all of Hawaiʻi's whales are (Herman 1979, p. 2); ${Math.round(w.depthM)} m down now, drawn near the surface (how deep they swim here is unread)`],
      ['Activity', `${w.calf ? 'with its calf, born here this winter (the game\'s calving, 19 February to 22 March); ' : ''}wintering, where whales "congregate and are believed to engage in mating and other social activities" (Bettridge et al. 2015, p. 7)`],
      ['Trend', 'held level at NOAA\'s estimate until its trend is coded: "it is unknown if this population is currently increasing" (NOAA\'s stock assessment)'],
      ['Migration', 'to its feeding grounds off Alaska and British Columbia in spring, back in winter, about 4,830 km, made in as little as 36 days (Gabriele et al. 1996, as Bettridge et al. 2015 give it, p. 7); its ways are on the world map'],
    ],
  };
}

/** A followed honu as the view and the simulation report it (M104): its sprite, its places and hours, the turtles it stands for. */
export interface FollowedHonu {
  sprite: { adult: boolean; cm: number; island: number };
  activity: 'resting' | 'leaving' | 'foraging' | 'returning';
  /** Its real depth now, its resting depth, m, and how far apart its places are drawn, km. */
  depthM: number;
  restM: number;
  wayKm: number;
  /** The hours, the sun's, it leaves its resting place and gets back to it. */
  hours: { leave: number; back: number };
  sprites: { young: number; adults: number };
  perSprite: number;
  young: number;
  adults: number;
  away: number;
}

/** A span's days as a player reads them, "20 March to 15 May". */
const spanLabel = (span: Span): string => [span.from, span.to].map(([m, d]) => dateLabel(epochDayFromCivil(2001, m, d)).replace(/ 2001$/, '')).join(' to ');

/** The card for a followed honu (M104): what it stands for, where, its day as the sources give it, its breeding and its trend. */
export function honuCard(h: FollowedHonu): WildlifeCard {
  const n = (x: number): string => Math.round(x).toLocaleString('en-US');
  const stage = h.sprite.adult ? 'adults' : 'young';
  const away = h.away > 0 ? `; the ${n(h.away)} adults away breeding are not drawn` : '';
  const island = ISLAND_NAMES[REEF_ISLANDS[h.sprite.island] ?? 'maui'];
  const day: Record<FollowedHonu['activity'], string> = {
    resting: `resting on the bottom ${Math.round(h.depthM)} m down, as one honu's dive recorder at Punaluʻu found it resting, 12.9 m down on average, deeper than 14 m only on uncommon, exploratory dives, not drawn (Rice et al.); others rest shallower, Kiholo's in a pond under 4 m deep, Kahului's at 2 m or less (Balazs et al.; Balazs 1996). It leaves to forage at ${clockLabel(h.hours.leave)} by the sun, "usually just before sunrise" (Balazs et al., Kiholo), most between 5 and 9 in the morning (Harrington et al., Kiholo)`,
    leaving: `swimming to forage in the shallows, an hour's way (Turtle 257's two hours a day of travel, Rice et al.)`,
    foraging: `foraging in the shallows, ${Math.round(h.depthM)} m down as drawn, at the shallowest water that holds its sprite; Hawaiʻi's honu forage mostly between 0 and 2 m (Rice et al.). Back to rest ${Math.round(h.restM)} m down by ${clockLabel(h.hours.back)} by the sun: they come back from the late afternoon (Balazs 1996) on into the night after twilight (Balazs et al. 1987)`,
    returning: `swimming back to rest ${Math.round(h.restM)} m down for the night, an hour's way (Rice et al.)`,
  };
  return {
    title: displayName(speciesOf('honu')),
    rows: [
      ['Stands for', `This sprite stands for ${n(h.perSprite)} of the ${n(h.sprite.adult ? h.adults : h.young)} ${stage} at home at Maui Nui today: the aquarium draws a sprite for each ${n(h.perSprite)} of each, rounded, ${h.sprites.young} young and ${h.sprites.adults} adults${away}. This one the size of the turtle in the middle of its share, ${Math.round(h.sprite.cm)} cm in the shell (straight carapace length), drawn to the fish's scale`],
      ['Here', `on ${island}'s coast, Maui Nui's honu shared among its islands by NOAA's towed surveys' turtles a tow segment and each island's coastline`],
      ['Activity', day[h.activity]],
      ['Drawn', `its resting place ${h.wayKm < 0.05 ? 'where it forages' : `${h.wayKm.toFixed(1)} km from where it forages`}, as the exhibit's floor puts their depths apart; Hawaiʻi's honu rest within about 300 to 500 m of where they feed (Davis et al., Puakō and Punaluʻu). Not drawn: the foraging some do at night, and their basking`],
      ['Breeding', `its adults leave ${spanLabel(HONU.leaveHome)} to breed at French Frigate Shoals, the males back ${spanLabel(HONU.malesHome)}, the females ${spanLabel(HONU.femalesHome)} (the game's breeding calendar, M93)`],
      ['Trend', 'held level: the young\'s survival at sea is fitted so Maui Nui keeps its share of the archipelago\'s adults, until its trend is coded'],
    ],
  };
}

/** A followed monk seal in the whole tank as the view and the simulation report it (M152): its sprite, its depth, the seals it stands for. */
export interface FollowedSeal {
  sprite: { stage: 'pup' | 'juvenile' | 'adult'; island: number };
  depthM: number;
  /** Its stage's standard length, m (`SEAL_BODY`). */
  lengthM: number;
  sprites: { pup: number; juvenile: number; adult: number };
  perSprite: number;
  /** Maui Nui's seals at sea by stage today, the nursing pups ashore apart, and the whole population. */
  atSea: { pup: number; juvenile: number; adult: number };
  nursing: number;
  population: number;
}

/** The card for a followed monk seal in the whole tank (M152, the user's word: "i cant select monk seal in whole aquarium view"). */
export function sealCard(s: FollowedSeal): WildlifeCard {
  const n = (x: number): string => Math.round(x).toLocaleString('en-US');
  const stages = { pup: 'weaned pups', juvenile: 'juveniles', adult: 'adults' } as const;
  const island = ISLAND_NAMES[REEF_ISLANDS[s.sprite.island] ?? 'maui'];
  // Its own stage's seals shared among its stage's sprites (M152's review: a sprite for each ten in all, shared among the
  // stages by largest remainder, so a stage of six weaned pups has one sprite for all six, not ten).
  const mine = s.atSea[s.sprite.stage] / Math.max(1, s.sprites[s.sprite.stage]);
  const share = mine >= 9.5 ? n(mine) : mine.toFixed(1).replace(/\.0$/, '');
  const nursing = s.nursing > 0 ? `; the ${n(s.nursing)} pups still nursing are ashore with their mothers, not drawn` : '';
  return {
    title: displayName(speciesOf('monkSeal')),
    rows: [
      ['Stands for', `This sprite stands for ${share} of the ${n(s.atSea[s.sprite.stage])} ${stages[s.sprite.stage]} at sea at Maui Nui today, of its ${n(s.population)} seals: the aquarium draws a sprite for each ${n(s.perSprite)} seals at sea, rounded, shared among the stages by their numbers, ${s.sprites.adult} adults, ${s.sprites.juvenile} juveniles and ${s.sprites.pup} weaned pups${nursing}`],
      ['Here', `on ${island}'s coast, Maui Nui's seals shared among its islands by each island's coastline (the simulation holds no island's own)`],
      ['Activity', `foraging near the bottom, the floor under it ${Math.round(s.depthM)} m down; in the main islands their foraging dives go a median 19.4 m down, the middle half 11.6 to 31.7 m (Wilson, Littnan and Read 2017), each sprite at its own depth across that half`],
      ['Size', `${stages[s.sprite.stage].replace(/s$/, '')}, about ${s.lengthM.toFixed(2)} m long, the mean of the seals of its stage measured at Laysan (Stewart and Yochem 2004); drawn shorter than the fish's scale would, so the honu, the seal and the whale keep their order of size`],
    ],
  };
}

/** A habitat's visitor followed (M152): its kind, its stage, its length and depth, m, the habitat's name, and how often one comes, in the panel's words. */
export interface FollowedVisitor {
  kind: 'honu' | 'honuea' | 'seal' | 'tigerShark' | 'whitetip' | 'manta';
  stage: 'adult' | 'young' | 'juvenile' | 'pup';
  /** A manta's sex (M242). */
  female?: boolean;
  lengthM: number;
  depthM: number;
  habitat: string;
  howOften: string;
  /** Its tag's name where it is a tagged animal drawn where its tag tells it is (M203); null or none for the day's visitor. */
  tagged?: string | null;
  /** Whether it is off the shelf, in the Blue Water (M211's review: a shark there was told of the shelf's floor under it). */
  offShelf?: boolean;
  /** A whitetip's activity now (M235, `whitetipActive`): 0 resting on the reef, 1 swimming it. */
  active?: number;
}

/** The card for a honu, a hawksbill (M196), a monk seal or a tiger shark (M197) visiting a habitat's scene (M152, the user's word: "i cant tap and select some sea creatures like monk seal and honu in habitat view"). */
export function visitorCard(v: FollowedVisitor): WildlifeCard {
  const stage = v.stage === 'adult' ? 'an adult' : v.stage === 'young' ? 'a young' : `a ${v.stage}`;
  const species = v.kind === 'honu' ? 'honu' : v.kind === 'honuea' ? 'honuea' : v.kind === 'tigerShark' ? 'tigerShark' : v.kind === 'whitetip' ? 'whitetip' : v.kind === 'manta' ? 'manta' : 'monkSeal';
  const turtle = v.kind === 'honu' || v.kind === 'honuea';
  const depth = {
    honu: "Hawaiʻi's honu forage mostly between 0 and 2 m and one at Punaluʻu rested 12.9 m down on average (Rice et al.)",
    honuea: "how deep Hawaiʻi's hawksbills forage is not yet in the game's sources",
    monkSeal: 'in the main islands their foraging dives go a median 19.4 m down, the middle half 11.6 to 31.7 m (Wilson, Littnan and Read 2017)',
    tigerShark: v.offShelf
      ? 'off the shelf: 21.3% of the satellite fixes of Hawaii\'s tracked tiger sharks, 217 to 448 cm long, were over water deeper than 500 m, some of them "spending months in open-ocean" (Meyer et al. 2018)'
      : 'at Maui the floor under their satellite fixes is most often 50 to 100 m down, where they dive "between the seabed and the surface" (Meyer et al. 2018)',
    whitetip: 'Hawaiʻi\'s whitetips are seen "At depths of about 25-130 ft near coral reefs" (DLNR), resting by day, hunting by night',
    manta: `off west Maui they gather ${MANTA.siteDepthM[0]} to ${MANTA.siteDepthM[1]} m down, over fringing reef, to be cleaned by Hawaiian cleaner wrasses and saddle wrasses (Deakos 2010)`,
  }[species];
  const named = { honu: 'honu', honuea: 'hawksbill', monkSeal: 'monk seal', tigerShark: 'tiger shark', whitetip: 'whitetip reef shark', manta: v.female ? 'female reef manta' : 'male reef manta' }[species];
  const size = turtle
    ? `${v.lengthM.toFixed(1)} m in the shell (straight carapace length)`
    : v.kind === 'tigerShark'
      ? `about ${v.lengthM.toFixed(1)} m long, the length Hawaii's tiger sharks grow to by its age (Meyer et al. 2014); ${v.stage === 'adult' ? 'grown' : 'not yet grown'}, a female mature by 5 years, a male by 4`
      : v.kind === 'whitetip'
      ? `about ${v.lengthM.toFixed(1)} m long, the length the Great Barrier Reef's whitetips grow to by its age (Hisano et al. 2011; no Hawaiian whitetip has been aged); ${v.stage === 'adult' ? 'grown' : 'not yet grown'}, a female mature at 8 years`
      : v.kind === 'manta'
      ? `about ${v.lengthM.toFixed(2)} m across the disc, the mean of the ${v.female ? 'females' : 'males'} measured with lasers off west Maui (Deakos 2010); ${v.female ? `a female mature by about ${MANTA.matureDiscM.female.toFixed(2)} m, larger than their mean, so not every one here grown` : `a male mature at about ${MANTA.matureDiscM.male[0].toFixed(2)} to ${MANTA.matureDiscM.male[1].toFixed(2)} m`}`
      : `about ${v.lengthM.toFixed(2)} m long, the mean of the seals of its stage measured at Laysan (Stewart and Yochem 2004)`;
  return {
    title: v.tagged ? `${v.tagged}, ${displayName(speciesOf(species))}` : displayName(speciesOf(species)),
    species,
    rows: [
      v.tagged
        ? ['Tagged', `${stage} ${named} you tagged, in ${v.habitat} today as its tag tells it; the Tagged list says where it is each day`]
        : v.kind === 'whitetip'
        ? ['Here', `${stage} ${named} at home on ${v.habitat} today, one of Maui Nui's, which keep to their own reef; one is in this hectare on ${v.howOften}, a draw a day`]
        : v.kind === 'manta'
        ? ['Visiting', `a ${named} visiting ${v.habitat} today, one of around ${MANTA.individualsSeen.toLocaleString('en-US')} told apart at Maui Nui since 2005 (NOAA); one comes here on ${v.howOften}, a draw a day, at the share of NOAA's divers' site visits on Maui Nui's reef that met one, here from the hour it comes to sunset`]
        : ['Visiting', `${stage} ${named} visiting ${v.habitat} today, one of Maui Nui's; one comes here on ${v.howOften}, a draw a day`],
      ['Size', size],
      ['Depth', `${Math.round(v.depthM)} m down now; ${depth}`],
      ...(v.kind === 'manta' ? [['Activity', 'here by day to be cleaned, more of them as the day goes on, gone offshore after sunset; none was seen feeding at the cleaning site (Deakos 2010)'] as [string, string]] : []),
      ...(v.kind === 'whitetip' ? [['Activity', (v.active ?? 1) < 0.05 ? 'resting on the reef by day, as whitetips rest in its caves (DLNR; Barnett et al. 2012)' : (v.active ?? 1) < 0.95 ? 'stirring at dusk, or settling at dawn' : 'swimming the reef by night, hunting its fish, octopuses and crustaceans (DLNR; Barnett et al. 2012)'] as [string, string]] : []),
    ],
  };
}

/**
 * A species' visitor followed in a habitat it visits (M147): its species, phase, length and depth, m, the habitat it is
 * in and its own, its kind's count there today and on the mean day, its density there against home's and whose count
 * that is, its slot's swap hour, and whether it is the one that came at the swap, or the one gone home at it.
 */
export interface FollowedSpeciesVisitor {
  species: VisitorSpecies;
  stage: 'adult' | 'terminal';
  lengthM: number;
  /** Its depth as drawn; null once gone home, out of the scene. */
  depthM: number | null;
  habitat: string;
  home: string;
  count: number;
  expected: number;
  perAreaOfHome: number;
  source: string;
  swapHour: number;
  after: boolean;
  gone: boolean;
  drawnTimes: number;
}

/** A day octopus followed on the Reef (M158): its weight, its island, its color now and the one it fades to, and the hectare's octopuses. */
export interface FollowedOctopus {
  weightKg: number;
  island: string;
  young: boolean;
  color: string;
  fadingTo: string | null;
  depthM: number;
  inHectare: number;
  outNow: number;
  perHa: number;
  mantleM: number;
  drawnTimes: number;
  /** The hour its forage ends (local solar). */
  backAt: number;
}

/** Each of the user's seven colors as Hanlon, Forsythe and Joneschild 1999 name the patterns (Table 2, p. 12; `src/render/habitats/octopuses.ts`), the red none of theirs. */
const OCTOPUS_PATTERNS: Readonly<Record<string, string>> = {
  mottled: 'mottled', pale: 'uniform light', dark: 'all dark', green: 'rugose, like algae on rock', passingCloud: 'passing cloud', whiteSpots: 'reticulated mottle, white-spotted', red: 'brick red',
};

/** The card for a day octopus out of its den on the Reef (M158). */
export function octopusCard(o: FollowedOctopus): WildlifeCard {
  const pattern = (c: string): string => OCTOPUS_PATTERNS[c] ?? c;
  return {
    title: SPECIES_NAMES.octopus,
    rows: [
      ['Doing', `on its ${o.backAt < 12 ? 'morning' : 'late afternoon'} forage, stop and go, ${Math.round(o.depthM)} m down, back in its den by ${clockLabel(o.backAt)}; it forages twice a day, at the start and the end of the day (Forsythe and Hanlon 1997; Ivey 2007), and changes its pattern 2.95 times a minute on average as it goes (Hanlon, Forsythe and Joneschild 1999), each change drawn fading over 2.5 seconds`],
      ['Pattern', o.fadingTo ? `${pattern(o.color)}, turning ${pattern(o.fadingTo)}` : pattern(o.color)],
      ['Size', `${o.weightKg < 1 ? `${Math.round(o.weightKg * 1000)} g` : `${o.weightKg.toFixed(1)} kg`}, ${o.young ? 'not yet mature' : 'mature'}, its mantle about ${Math.round(o.mantleM * 100)} cm long (as Hawaiʻi's octopuses measure for their weight, Ivey 2007), drawn ${o.drawnTimes} times its size to be seen beside the reef's fish`],
      ['Here', `its den on ${o.island}'s reef; ${o.inHectare} in this hectare, one to a den (${o.perHa.toFixed(1)} a hectare of Maui Nui's reef to 30 m on average), ${o.outNow} out now`],
    ],
  };
}

/** The card for a reef fish, or a humpback (M210), visiting a habitat other than its own (M147), with the way home where it has gone there. */
export function speciesVisitorCard(v: FollowedSpeciesVisitor): WildlifeCard {
  const size = v.species === 'humpback'
    ? `about ${v.lengthM.toFixed(1)} m long, the mean NOAA's drones measured off Maui Nui of its juveniles and adults (van Aswegen 2025), drawn at its true length`
    : `${v.lengthM.toFixed(2)} m long, its ${v.stage === 'terminal' ? 'terminal males\'' : 'adults\''} mean at Maui Nui today, drawn ${v.drawnTimes} times its length as on the Reef`;
  const ratio = `${v.perAreaOfHome < 0.1 ? v.perAreaOfHome.toFixed(3) : v.perAreaOfHome.toFixed(2)} times as many of them a square meter as ${v.home}`;
  const mean = v.expected >= 1 ? `${v.expected.toFixed(1)} on the mean day` : `one on about one day in ${Math.round(1 / Math.max(1e-9, v.expected)).toLocaleString('en-US')}`;
  const at = clockLabel(v.swapHour);
  return {
    title: displayName(speciesOf(v.species)),
    species: v.species,
    rows: [
      ['Visiting', `${v.count === 1 ? 'the one' : `one of ${v.count}`} visiting ${v.habitat} today from ${v.home} (${mean}): ${v.species === 'humpback' ? 'boat surveys counted' : 'divers counted'} ${ratio} (${v.source})`],
      ['Its way', v.gone ? `swam back to ${v.home} at ${at}, another coming from there in its place` : v.after ? `came from ${v.home} at ${at} in place of one gone back there; stays the rest of the day` : `goes back to ${v.home} at ${at}, another coming from there in its place`],
      ['Size', size],
      ...(v.depthM === null ? [] : [['Depth', `${Math.round(v.depthM)} m down now`] as const]),
    ],
    ...(v.gone ? { onward: `Follow it to ${v.home}` } : {}),
  };
}

/** A Deep 7 fish followed on the Slope's floor (M215). */
export interface FollowedSlopeFish {
  species: Deep7SpeciesId;
  forkCm: number;
  kg: number;
  inHectare: number;
  perHa: number;
  /** How deep it is drawn, m. */
  depthM: number;
  drawnTimes: number;
}

/**
 * The card for a Deep 7 fish (M215): the day's fish of its species in the Slope's hectare, its fish a hectare of Maui
 * Nui's hard bottom, its depths and its size. No tag: the stock is held as one, by its biomass, with no fish of it to
 * follow (M192).
 */
export function slopeFishCard(f: FollowedSlopeFish): WildlifeCard {
  const s = DEEP7_SPECIES.find((x) => x.id === f.species);
  const name = displayName(speciesOf(f.species));
  return {
    title: name,
    species: f.species,
    rows: [
      ['Here', `${f.inHectare === 1 ? 'the one' : `one of ${f.inHectare}`} in this hectare of the Slope's hard bottom today; ${f.perHa < 1 ? f.perHa.toFixed(2) : f.perHa.toFixed(1)} a hectare of Maui Nui's on average, the Deep 7's stock spread over its ${DEEP7_MAUI_NUI_HARD_KM2} km² of hard bottom at 100 to 400 m, each species its share of NOAA's 2024 survey (Richards 2025)`],
      ['Depth', `most at ${s?.depthM[0]} to ${s?.depthM[1]} m (${f.species === 'lehi' || f.species === 'gindai' || f.species === 'hapuupuu' ? 'Ralston and Polovina 1982' : 'NOAA\'s bottom cameras, Misa et al. 2013'}); drawn ${Math.round(f.depthM)} m down, the Slope drawn at its middle, the shallower species up the slope and the deeper down it`],
      ['Size', `about ${Math.round(f.forkCm)} cm to the fork of its tail and ${f.kg.toFixed(1)} kg, the survey's mean of its fish 29 cm and longer, drawn ${f.drawnTimes} times its length as the reef's fish are`],
      ['Fished', 'by hook and line, the Deep 7 assessed and managed as one stock (Syslo et al. 2024)'],
    ],
  };
}

/** A spinner dolphin followed over the Reef (M216). */
export interface FollowedDolphin {
  inHectare: number;
  groupMean: number;
  /** The odds a group rests over the hectare on a day. */
  odds: number;
  lengthM: number;
  depthM: number;
}

/**
 * The card for a spinner dolphin resting over the Reef (M216): its group's dolphins in the hectare, how often a group
 * rests there, how the dolphins live, and its size. No tag: no count of Maui Nui's is read (M188).
 */
export function dolphinCard(d: FollowedDolphin): WildlifeCard {
  return {
    title: displayName(speciesOf('spinnerDolphin')),
    species: 'spinnerDolphin',
    rows: [
      ['Here', `one of ${d.inHectare} of its group over this hectare of the reef, its group about ${Math.round(d.groupMean)} on average at Maui Nui (Olson et al. 2026); a group rests over a hectare of the reef on about one day in ${Math.round(1 / Math.max(1e-9, d.odds)).toLocaleString('en-US')}: Maui Nui's recorders heard them near shore in ${Math.round(SPINNER_DOLPHIN.heardHourShare * 100)}% of hours (McElligott and Lammers 2021), a group covering a share of that reach (Olson et al. 2026)`],
      ['Doing', 'resting: it comes in from the open water in the morning and rests through the middle of the day, swimming slowly with its group, and goes back out late in the afternoon to hunt over the slope at night'],
      ['Size', `about ${d.lengthM.toFixed(1)} m long, drawn at its true length`],
      ['Depth', `${Math.round(d.depthM)} m down now`],
    ],
  };
}
